import { z } from 'zod'
import { oauthScopeSchema } from '../enums/oauth-scope.enum.js'
import { instantSchema } from '../validators/temporal.validator.js'

/**
 * Why `/oauth/authorize` rendered a page here instead of sending the browser on.
 *
 * A fixed set, because the query string that carries one is something anybody
 * can write. The page shows one of these or its own fallback — never the raw
 * parameter.
 */
export const OAUTH_PROBLEM_REASON = {
  unknownClient: 'That application isn’t set up to connect here.',
  unregisteredRedirect: 'That application asked to send you somewhere it isn’t set up to use.',
} as const

const OAUTH_PROBLEM_REASONS = [
  OAUTH_PROBLEM_REASON.unknownClient,
  OAUTH_PROBLEM_REASON.unregisteredRedirect,
] as const

export const oauthProblemReasonSchema = z.enum(OAUTH_PROBLEM_REASONS)

export type OAuthProblemReason = z.infer<typeof oauthProblemReasonSchema>

/**
 * An authorization waiting for an answer, as the consent screen needs it.
 *
 * Read back from the row rather than carried in the URL that reached the
 * screen — the same rule, for the same reason, as `redirectTo` on a magic-link
 * token. Parameters in a URL are parameters the person approving can edit:
 * raise the scopes, change where the code goes. On the row they are fixed at
 * the moment of issue and this is only a display of them.
 */
export const pendingAuthorizationDtoSchema = z.object({
  id: z.string().uuid(),
  /**
   * What the application calls itself, taken from its own registration.
   *
   * Somebody else's text on our page. Bounded when it was registered and
   * escaped where React renders it; never anything but a label.
   */
  clientName: z.string(),
  /** The `resource:action` scopes it asked for, already narrowed to ours. */
  scopes: z.array(oauthScopeSchema),
  /** When the request lapses unanswered. */
  expiresAt: instantSchema,
  /**
   * Whether the signed-in account may connect an application at all.
   *
   * False for a platform super admin, whose connection would reach every
   * organization, and for a client, whose access exists to see their own event.
   * Both are settled rules — see `docs/api-tokens.md` — and the screen has to
   * know them *before* it offers a form, or somebody ticks three boxes and is
   * told they ticked nothing.
   */
  canConnect: z.boolean(),
  /** Why not, in words for the person. Null when they can. */
  refusal: z.string().nullable(),
})

export type PendingAuthorizationDto = z.infer<typeof pendingAuthorizationDtoSchema>

/**
 * Where to send the browser once the person has decided.
 *
 * An absolute URL back to the application, which the screen navigates to rather
 * than fetches. It is the client's own registered address — matched exactly at
 * `/oauth/authorize` before this request ever existed.
 */
export const authorizationDecisionDtoSchema = z.object({
  redirectTo: z.string().url(),
})

export type AuthorizationDecisionDto = z.infer<typeof authorizationDecisionDtoSchema>
